'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Check, Eye, EyeOff, X } from 'lucide-react';
import { fmtMoney } from '@/lib/format';
import { HIDDEN_AMOUNT, setAmountsHidden, useAmountsHidden } from '@/lib/hideAmounts';
import { usePortalContainer } from '@church/nextjs-ui/bottom-sheet';
import { BTN_QUIET, CTA_SMALL, Pii } from '@church/nextjs-ui/website/profile';

// The cards, rows, switches and sheet live in the UI package, shared with the
// staff tools; this file keeps what is the widget's own (giving amounts, the
// toast and the confirm dialog) and re-exports the rest so imports stay put.
export {
  BTN_QUIET,
  Bar,
  CARD,
  CHIP,
  CHIP_DASHED,
  CHIP_IDLE,
  CHIP_SELECTED,
  CLOSE_BUTTON,
  CTA,
  CTA_DANGER,
  CTA_SMALL,
  CTA_TEAL,
  CardsSkeleton,
  Chip,
  DISPLAY,
  EditButton,
  Empty,
  FIELD,
  Fact,
  FactCard,
  FactGrid,
  FormLook,
  HeadAction,
  Heading,
  InlineError,
  Kicker,
  LABEL,
  LINK,
  Masonry,
  MasonryItem,
  PageBody,
  Pii,
  ProfileSheet,
  Row,
  RowCard,
  RowsSkeleton,
  SavedMark,
  Section,
  SwitchRow,
} from '@church/nextjs-ui/website/profile';

/**
 * A giving amount: marked personal (see Pii), and dots instead of the figure
 * while "Hide amounts" is on.
 */
export function Money({ value, className = '' }: { value: number; className?: string }) {
  const hidden = useAmountsHidden();
  return <Pii className={className}>{hidden ? HIDDEN_AMOUNT : fmtMoney(value)}</Pii>;
}

/** The eye that hides and shows every giving amount. Stops the click reaching the card under it. */
export function HideAmountsToggle({ onDark = false }: { onDark?: boolean }) {
  const hidden = useAmountsHidden();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setAmountsHidden(!hidden);
      }}
      onKeyDown={(e) => e.stopPropagation()}
      aria-pressed={hidden}
      aria-label={hidden ? 'Show amounts' : 'Hide amounts'}
      title={hidden ? 'Show amounts' : 'Hide amounts'}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition-all hover:scale-105 active:scale-95 ${
        onDark
          ? 'bg-white/15 text-white hover:bg-white/25'
          : 'bg-gray-100 text-[#1c2b39] hover:bg-gray-200'
      }`}
    >
      {hidden ? (
        <EyeOff className="h-3.5 w-3.5" strokeWidth={2.5} />
      ) : (
        <Eye className="h-3.5 w-3.5" strokeWidth={2.5} />
      )}
      {hidden ? 'Show' : 'Hide'}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Toast: one liquid-glass pill at the bottom of the viewport          */
/* ------------------------------------------------------------------ */

interface ToastState {
  id: number;
  message: string;
  tone: 'error' | 'ok';
}

const ToastContext = createContext<(message: string, tone?: 'error' | 'ok') => void>(() => {});

export function useToast() {
  return useContext(ToastContext);
}

/** What a confirmation asks. */
export interface ConfirmRequest {
  title: string;
  body?: string;
  confirmLabel: string;
  cancelLabel?: string;
}

const ConfirmContext = createContext<(request: ConfirmRequest) => Promise<boolean>>(() =>
  Promise.resolve(true)
);

/**
 * Ask before a change that is easy to make by accident and matters — the
 * Email Subscriptions and texting switches. Resolves true on confirm, false on
 * cancel, Escape or a tap outside. Hosted by ToastProvider, so it portals over
 * any open sheet the same way the toast does.
 */
export function useConfirm() {
  return useContext(ConfirmContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<number | null>(null);
  const portalContainer = usePortalContainer();
  const [ask, setAsk] = useState<(ConfirmRequest & { resolve: (ok: boolean) => void }) | null>(
    null
  );

  const confirm = useCallback(
    (request: ConfirmRequest) =>
      new Promise<boolean>((resolve) => {
        setAsk({ ...request, resolve });
      }),
    []
  );
  const answer = useCallback(
    (ok: boolean) => {
      ask?.resolve(ok);
      setAsk(null);
    },
    [ask]
  );
  useEffect(() => {
    if (!ask) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        answer(false);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [ask, answer]);

  const show = useCallback((message: string, tone: 'error' | 'ok' = 'error') => {
    if (timer.current) window.clearTimeout(timer.current);
    const id = Date.now();
    setToast({ id, message, tone });
    timer.current = window.setTimeout(() => setToast((t) => (t?.id === id ? null : t)), 3500);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    []
  );

  const node = toast ? (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[70] flex justify-center px-4">
      <div
        role="status"
        className={`pointer-events-auto inline-flex max-w-full items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold shadow-lg backdrop-blur ${
          toast.tone === 'error'
            ? 'bg-white/90 text-red-600 ring-1 ring-red-200'
            : 'bg-[#1c2b39]/90 text-white'
        }`}
      >
        {toast.tone === 'error' ? (
          <AlertCircle className="h-4 w-4 shrink-0" strokeWidth={2.5} />
        ) : (
          <Check className="h-4 w-4 shrink-0" strokeWidth={3} />
        )}
        <span className="min-w-0 truncate">{toast.message}</span>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => setToast(null)}
          className="ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full opacity-70 hover:opacity-100"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  ) : null;

  // Above the sheets (z-50) and the toast (z-[70]).
  const dialog = ask ? (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-[#1c2b39]/40 p-4 backdrop-blur-sm sm:items-center"
      onClick={() => answer(false)}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="profile-confirm-title"
        className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <p id="profile-confirm-title" className="text-lg font-bold text-[#1c2b39]">
          {ask.title}
        </p>
        {ask.body && <p className="mt-2 text-sm text-gray-600">{ask.body}</p>}
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={() => answer(false)} className={BTN_QUIET}>
            {ask.cancelLabel ?? 'Cancel'}
          </button>
          <button
            type="button"
            autoFocus
            onClick={() => answer(true)}
            className={`${CTA_SMALL} bg-[#299c8f] text-white`}
          >
            {ask.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <ToastContext.Provider value={show}>
      <ConfirmContext.Provider value={confirm}>
        {children}
        {node && (portalContainer ? createPortal(node, portalContainer) : node)}
        {dialog && (portalContainer ? createPortal(dialog, portalContainer) : dialog)}
      </ConfirmContext.Provider>
    </ToastContext.Provider>
  );
}
